import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingBloubergstrandPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Bloubergstrand Professional Painters - House Interior & Exterior Painting | Roof Specialists',
    description: 'Expert house painting services in Bloubergstrand, Table View, and West Beach. Specialized in interior, exterior, and roof painting with weather-resistant coatings. Over 16 years of coastal painting experience.',
    keywords: 'painters Bloubergstrand, house painters Bloubergstrand, interior painting, exterior painting, roof painting, coastal painting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Bloubergstrand',
          description: 'House painting and roof specialists in Bloubergstrand',
          address: 'Bloubergstrand, Cape Town',
          priceRange: '$$',
          serviceArea: ['Bloubergstrand', 'Table View', 'West Beach', 'Big Bay', 'Sunningdale']
        }),
        generateServiceSchema({
          name: 'House & Roof Painting Services',
          description: 'Complete house painting including roof treatments',
          provider: 'Painters Bloubergstrand',
          areaServed: ['Bloubergstrand', 'Table View', 'West Beach'],
          serviceType: ['House Interior Painting', 'House Exterior Painting', 'Roof Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Bloubergstrand",
    location: "Bloubergstrand, Cape Town",
    website: "https://paintersbloubergstrand.co.za/",
    heroImage: "https://public.readdy.ai/ai/img_res/926bb85e5a59223bafa255af17aa06a6.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20coastal%20home%20exterior%20white%20walls%20with%20ocean%20view%20modern%20architecture%20bloubergstrand%20beach%20setting%20clear%20blue%20sky&width=800&height=600&seq=gallery-blou1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=interior%20room%20painting%20with%20coastal%20theme%20light%20colors%20professional%20finish%20modern%20furniture%20ocean%20inspired%20decor%20natural%20lighting&width=800&height=600&seq=gallery-blou2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=roof%20painting%20project%20on%20coastal%20property%20with%20ocean%20views%20professional%20painters%20working%20weather%20resistant%20coating%20application&width=800&height=600&seq=gallery-blou3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=freshly%20painted%20beachfront%20apartment%20building%20exterior%20white%20and%20blue%20color%20scheme%20modern%20coastal%20architecture%20professional%20finish&width=800&height=600&seq=gallery-blou4&orientation=landscape"
    ],
    services: [
      {
        title: "House Interior Painting",
        description: "Transform your home's interior with our expert painting services. We specialize in creating beautiful, lasting finishes that enhance your living spaces and reflect your personal style.",
        features: ["Living rooms & bedrooms", "Kitchens & bathrooms", "Feature walls & accent colors", "Ceiling painting", "Trim & skirting boards", "Color consultation"]
      },
      {
        title: "House Exterior Painting",
        description: "Protect and beautify your home's exterior with our professional painting services. We use premium weather-resistant paints specifically formulated for coastal conditions.",
        features: ["Facade painting", "Weatherboard & cladding", "Brick & masonry", "Window frames & doors", "Coastal weather protection", "Salt damage prevention"]
      },
      {
        title: "Roof Painting",
        description: "Extend the life of your roof and improve your home's appearance with our specialized roof painting and coating services. Essential for coastal properties.",
        features: ["Roof cleaning & preparation", "Waterproofing treatments", "Heat-reflective coatings", "Tile & metal roof painting", "Leak prevention", "UV protection"]
      }
    ],
    serviceAreas: ["Bloubergstrand", "Table View", "West Beach", "Sunset Beach", "Big Bay", "Dolphin Beach"],
    about: "Painters Bloubergstrand is a premier painting company specializing in coastal properties in the Bloubergstrand area and surrounding suburbs. With over 14 years of experience serving the Cape Town coastal communities, we have developed specialized expertise in dealing with the unique challenges that coastal weather conditions present to painted surfaces.",
    aboutExtended: [
      "Our team understands that coastal properties face harsh environmental conditions including salt spray, strong winds, intense UV radiation, and moisture. We use only the highest quality marine-grade and weather-resistant paints and coatings specifically designed to withstand these challenging conditions. This specialized approach ensures your property maintains its beautiful appearance and protection for years to come.",
      "We offer comprehensive painting services for both residential and commercial properties, including interior painting, exterior painting, roof painting and waterproofing. Our painters are fully trained in the latest application techniques and safety procedures, and we maintain full insurance coverage for your complete peace of mind.",
      "What distinguishes us is our commitment to thorough surface preparation, which is critical for coastal properties. We take the time to properly clean, repair, and prime all surfaces before painting, ensuring optimal adhesion and longevity. Our attention to detail and dedication to quality workmanship has earned us an excellent reputation throughout the Bloubergstrand community."
    ],
    ourApproach: [
      "Comprehensive property assessment and consultation",
      "Detailed surface preparation including cleaning and repairs",
      "Salt damage treatment and prevention",
      "Premium marine-grade paints and coatings",
      "Professional application with attention to detail",
      "Weather-resistant finishing techniques",
      "Thorough cleanup and final inspection",
      "Ongoing support and maintenance advice"
    ],
    whyChoose: [
      "14+ years coastal painting experience",
      "Specialized in beachfront properties",
      "Marine-grade weather-resistant paints",
      "Salt damage prevention expertise",
      "Fully licensed and insured",
      "Free color consultation",
      "Competitive pricing",
      "Quality workmanship guaranteed",
      "Local Bloubergstrand team",
      "Excellent customer reviews"
    ],
    testimonials: [
      {
        name: "David Thompson",
        rating: 5,
        text: "Outstanding service! They really understand coastal painting. Our beachfront home looks amazing and the paint has held up perfectly despite the harsh weather. Highly recommended!"
      },
      {
        name: "Samantha Botha",
        rating: 5,
        text: "Professional team from start to finish. They took extra care with preparation and used excellent quality paints. Our house has never looked better!"
      },
      {
        name: "James Wilson",
        rating: 5,
        text: "Very impressed with their knowledge of coastal painting challenges. They addressed all our concerns about salt damage and the results are fantastic. Great value for money."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Bloubergstrand</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Coastal Painting Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Bloubergstrand</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following coastal areas in Cape Town:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Coastal Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer specialized painting services designed for coastal properties and challenging weather conditions.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Bloubergstrand, Table View, West Beach</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: 9AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
